import type { HomeIntlPage } from '@/utils/home-intl/types';

import { PRODUCTION_LOCALE_SET, PRODUCTION_ORIGIN, productionHref, productionLocale } from './production-locale';
import { APPROVED_PAGE_IDS, approvedPageRoute } from './routes';
import { localizeStoreHref } from './store-links';
import type { ApprovedPageData } from './types';

export type { ProductionLocale } from './production-locale';
export { PRODUCTION_FALLBACK_LOCALE, PRODUCTION_LOCALE_BY_LANGUAGE, PRODUCTION_LOCALES, PRODUCTION_ORIGIN, productionHref, productionLocale } from './production-locale';

/**
 * Single link resolver for every country/language page (/<geo>/<lang>/...): header, footer, menus, shared CTAs, approved
 * CTAs (`target_page`), inline links of approved prose and the Technology catalogue all go through here.
 *
 * Owner rules (06/10/2026):
 * 1. A link to one of the 29 page identities goes to the SAME pair (/<geo>/<lang>/<route>) when that page is approved and
 *    published for the pair (`pages`); otherwise to the same page on production (https://noping.com/<locale>/<route>).
 * 2. Every other destination (pricing, affiliates, blog/articles, trial, tutorials, legal, about, careers, beta,
 *    feedback, comparisons, Multi Internet/Connection, login/dashboard...) goes to production, in the production locale
 *    closest to the pair's language (production-locale.ts).
 * 3. Same-page anchors (#...) and foreign absolute URLs are kept as authored, except:
 * 4. NoPing's App Store / Google Play links, which open the store of the pair's country and language (store-links.ts,
 *    owner rule 07/10/2026): apps.apple.com/<cc>/app/noping/id6711342901, play.google.com/...&hl=<lang>&gl=<CC>.
 *
 * Client-safe: no fs, no manifest import. Availability comes from the server (ApprovedPageData.availablePages or
 * HomeNovaV3Info.pages).
 */

/** Destinations outside the 29 pages: always production. */
export const EXTERNAL_PATHS = {
  pricing: '/pricing',
  affiliates: '/affiliates',
  blog: '/blog',
  trial: '/trial',
  'tutorial-windows': '/tutorial/windows',
  'tutorial-ios': '/tutorial/ios',
  'tutorial-android': '/tutorial/android',
  privacy: '/terms/privacy-policy',
  terms: '/terms/terms-of-use',
  about: '/aboutus',
  'work-with-us': '/work-with-us',
  beta: '/beta-tester',
  evaluate: '/evaluate-our-platform',
  suggestion: '/feature-suggestion',
  'multi-internet': '/features/multi-internet',
  'multi-connection': '/features/multi-connection',
  dashboard: '/dashboard/overview',
  'support-ticket': '/support/ticket',
  exitlag: '/exitlag',
  lagofast: '/lagofast',
  wtfast: '/wtfast',
  gearup: '/gearup',
  mudfish: '/mudfish',
} as const;
export type ExternalKind = keyof typeof EXTERNAL_PATHS;

/**
 * Production blog: the index and the article pages (the footer's guides, src/constants/footer-data.ts FOOTER_BLOG_HREFS;
 * the test keeps both lists equal). In English their canonical has no locale (https://noping.com/guia-reduzir-lag, the
 * site's default locale), while /en/guia-reduzir-lag answers 200 with that canonical (LINK2): link straight to it.
 */
export const PRODUCTION_BLOG_ARTICLE_PATHS: readonly string[] = ['/guia-reduzir-lag', '/jitter', '/jogos-competitivos', '/melhorar-ping', '/reduzir-packet-loss'];
const isProductionBlogPath = (pathname: string) => pathname === '/blog' || pathname.startsWith('/blog/') || PRODUCTION_BLOG_ARTICLE_PATHS.includes(pathname);

/** productionHref, but a blog page in English goes to its canonical URL, without /en. */
function productionSiteHref(language: string | null | undefined, pathname: string): string {
  if (isProductionBlogPath(pathname) && productionLocale(language) === 'en') return `${PRODUCTION_ORIGIN}${pathname}`;
  return productionHref(language, pathname);
}

export function externalHref(kind: ExternalKind, language: string | null | undefined): string {
  return productionSiteHref(language, EXTERNAL_PATHS[kind]);
}

/** What a page needs to resolve links: its pair prefix (/br/pt), its language and the page ids published for it. */
export interface LinkPair {
  prefix: string;
  language: string;
  /** Page ids (of the 29, 'home' included) approved and published for this exact pair. */
  pages: readonly string[];
}

/**
 * Link context of an approved page. `availablePages` comes from the server (approvedPagesFor); older props without it are
 * read back from `hrefs` (a page id whose href is the pair route is published there).
 */
export function approvedLinkPair(data: Pick<ApprovedPageData, 'pair' | 'hrefs' | 'availablePages'>): LinkPair {
  const prefix = data.pair.path;
  const pages =
    data.availablePages ??
    APPROVED_PAGE_IDS.filter((pageId) => {
      const route = approvedPageRoute(pageId);
      return route !== null && data.hrefs[pageId] === `${prefix}${route}`;
    });
  return { prefix, language: data.pair.grupo, pages };
}

/** Link context of a Home v3 pair page (the Home itself is published; the other ids come from HomeNovaV3Info.pages). */
export function homeIntlLinkPair(page: Pick<HomeIntlPage, 'path' | 'grupo' | 'homeNovaV3'>): LinkPair {
  return { prefix: page.path, language: page.grupo, pages: ['home', ...(page.homeNovaV3?.pages ?? [])] };
}

/** Legacy site paths that name one of the 29 pages under another route. */
const PAGE_ALIASES: Readonly<Record<string, string>> = { '/pro-settings': 'pro-settings' };

const PAGE_ID_BY_ROUTE = new Map<string, string>(APPROVED_PAGE_IDS.map((id) => [approvedPageRoute(id) || '/', id]));

/** Page id named by a site path ('/', '/games', '/features/boost-fps', '/pro-settings'), or null. */
export function pageIdFromPath(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return PAGE_ALIASES[clean] ?? PAGE_ID_BY_ROUTE.get(clean) ?? null;
}

export const isApprovedPageId = (value: string) => APPROVED_PAGE_IDS.some((id) => id === value);

/** Rule 1: the pair's own page when published, else the same page on production. */
export function resolvePageHref(pageId: string, pair: LinkPair): string {
  const route = approvedPageRoute(pageId);
  if (route === null) throw new Error(`Unknown page identity: ${pageId}`);
  if (pair.pages.includes(pageId)) return `${pair.prefix}${route}`;
  return productionHref(pair.language, route || '/');
}

/** Path (with locale prefix removed) of a production URL (https://noping.com/pt/trial → /trial), or null. */
function productionPath(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!['noping.com', 'www.noping.com'].includes(url.hostname) || !['http:', 'https:'].includes(url.protocol)) return null;
  const segments = url.pathname.split('/');
  const first = segments[1] ?? '';
  let pathname: string;
  if (PRODUCTION_LOCALE_SET.has(first)) pathname = `/${segments.slice(2).join('/')}`;
  // Unprefixed English production paths are recognised only when they name a known page; anything else on that host
  // (e.g. a pair URL https://noping.com/br/pt) is kept exactly as authored.
  else if (pageIdFromPath(url.pathname) || Object.values(EXTERNAL_PATHS).some((path) => path === url.pathname)) pathname = url.pathname;
  else return null;
  return `${pathname}${url.search}${url.hash}`;
}

/**
 * Any authored destination → final href for this pair:
 * - '#anchor' and foreign absolute URLs (other hosts, mailto:, download.noping.com...) are kept, but a NoPing App Store /
 *   Google Play URL becomes the store of the pair's country and language (store-links.ts);
 * - a bare page id ('packet-loss', as used in approved inline links) or a path naming one of the 29 → resolvePageHref;
 * - a path already inside this pair (/br/pt/games) is re-resolved, so an unpublished page never leaks;
 * - https://noping.com/<locale>/... is re-resolved for this pair's language;
 * - every other site path ('/pricing', '/blog', '/trial', '/tutorial/ios'...) → production in the closest locale.
 */
export function resolveSiteHref(href: string, pair: LinkPair): string {
  const value = href.trim();
  if (!value || value.startsWith('#')) return value;
  if (isApprovedPageId(value)) return resolvePageHref(value, pair);
  let path = value;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('//')) {
    const fromProduction = productionPath(value);
    // Rule 4: a NoPing App Store / Google Play link opens the store of the pair's country and language.
    if (fromProduction === null) return localizeStoreHref(value, pair.prefix) ?? value;
    path = fromProduction;
  }
  if (!path.startsWith('/')) return value;
  const cut = path.search(/[?#]/);
  let pathname = cut < 0 ? path : path.slice(0, cut);
  const suffix = cut < 0 ? '' : path.slice(cut);
  if (pathname === pair.prefix || pathname.startsWith(`${pair.prefix}/`)) pathname = pathname.slice(pair.prefix.length) || '/';
  const pageId = pageIdFromPath(pathname);
  if (pageId) return `${resolvePageHref(pageId, pair)}${suffix}`;
  return `${productionSiteHref(pair.language, pathname)}${suffix}`;
}

/** The href is a navigation to another document on another origin (render a plain <a>, not next/link). */
export const isAbsoluteHref = (href: string) => /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//');
